/* eslint-disable react-doctor/no-inline-exhaustive-style, react-doctor/no-tiny-text */
'use client'

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'

import SoundCard from '@/components/ui/sound-card'
import { MORE_TRENDING_HREF } from '@/lib/homepage-rails'
import { InFeedAd } from '@/components/ui/ad-placement'
import type { Sound } from '@/lib/supabase-types'
import {
  buildTasteVector,
  reorderByTaste,
  reorderVibeBlocks,
  scoreSound,
  type Signal,
  type TasteVector,
} from '@/lib/personalization'
import { useFavoriteSounds } from '@/hooks/useFavoriteSounds'
import { useRecentPlays } from '@/hooks/useRecentPlays'
import { announce } from '@/lib/announce'

/**
 * "Growing list" semantics for personalized rails.
 *
 * Plain swap-on-fetch (the old behavior) is jarring: as the user plays
 * sounds, the recommender produces new results and the rail instantly
 * replaces what was there. Users lose the context of what they were
 * looking at AND don't realize the new entries are personalized for
 * their last interaction.
 *
 * This hook accumulates results across fetches:
 *   - Items already in the list stay put (preserves visual continuity).
 *   - New items are appended; their ids are returned in `newIds`.
 *   - Caller applies an animation class to cards whose id is in newIds.
 *   - `newIds` clears after `revealMs` so the animation only runs once.
 *
 * cap = total items kept in the rail (older entries fall off the front).
 */
export function useGrowingList<T extends { id: string }>(
  latest: T[] | null,
  cap = 12,
  revealMs = 1900,
): { items: T[]; newIds: Set<string> } {
  const [items, setItems] = useState<T[]>([])
  const [newIds, setNewIds] = useState<Set<string>>(new Set())
  // TES-1873: mirror of `items` so the merge below can read the current list
  // without a `setItems(prev => …)` updater.
  //
  // The merge used to live inside that updater, and `setNewIds` plus the
  // reveal `setTimeout` were called from in there. React runs updaters during
  // the render phase, so both were render-phase side effects — the same defect
  // fixed in `app/sounds/use-filter-sync.ts`, and a render-phase update is the
  // only path on which React swaps in its re-render dispatcher, whose
  // `updateWorkInProgressHook` is the sole thrower of "Rendered more hooks
  // than during the previous render." React is also free to run an updater
  // more than once (StrictMode double-invokes them precisely to surface impure
  // ones), which double-appended every fetch.
  //
  // Keeping the updater out of the picture means every side effect runs in
  // effect scope, where it belongs.
  const itemsRef = useRef(items)

  useEffect(() => {
    if (!latest || latest.length === 0) return
    const prev = itemsRef.current
    const seen = new Set(prev.map((p) => p.id))
    const fresh = latest.filter((x) => !seen.has(x.id))
    if (fresh.length === 0) return
    const merged = [...prev, ...fresh]
    // Keep the most recent `cap` so the rail doesn't grow unbounded.
    const trimmed = merged.length > cap ? merged.slice(merged.length - cap) : merged
    itemsRef.current = trimmed
    setItems(trimmed)
    setNewIds(new Set(fresh.map((f) => f.id)))
  }, [latest, cap])

  // Clear the reveal marker once its animation window has passed.
  //
  // Keyed on `newIds` rather than tracked through a ref'd timer handle: the
  // effect above is guarded by `itemsRef`, so it is a no-op on a second run,
  // and a ref'd timer torn down by a cleanup would never be re-armed. React
  // remounts effects on mount under StrictMode, which stranded the marker on
  // screen permanently. Deriving the timer from the marker itself makes the
  // teardown/re-arm cycle idempotent, and covers unmount and back-to-back
  // fetches with the same cleanup.
  useEffect(() => {
    if (newIds.size === 0) return
    const timer = setTimeout(() => setNewIds(new Set()), revealMs)
    return () => clearTimeout(timer)
  }, [newIds, revealMs])

  return { items, newIds }
}

export interface VibeBlock {
  vibeSlug: string
  vibeName: string
  picks: Sound[]
}

export interface ForYouFeedClientProps {
  trending: Sound[]
  blocks: VibeBlock[]
}

// The badge escalates through the INK steps, not into a hue. Personalization
// strength is a status, and DS §01.2 gives the accent exactly six roles —
// status is not one of them, and the system carries no semantic colour at
// all ("affirmative verdict = ink at 700"). Cold → subtle, warm → muted,
// hot → strong reads as the same three-step ramp without spending the accent.
const SIGNAL_COPY: Record<Signal, { label: string; tone: string }> = {
  cold: {
    label: 'Curated picks: play or save sounds to personalize',
    tone: 'var(--tls-fg-subtle)',
  },
  warm: {
    label: 'Personalizing: keep playing to sharpen your feed',
    tone: 'var(--tls-fg-muted)',
  },
  hot: {
    label: 'Personalized for your saves and recent plays',
    tone: 'var(--tls-fg-strong)',
  },
}

// eslint-disable-next-line react-doctor/no-giant-component -- for-you feed orchestrates multiple personalization rails; splitting requires significant prop-drilling refactor
export default function ForYouFeedClient({
  trending,
  blocks,
}: ForYouFeedClientProps) {
  const { favoriteIds, hasHydrated: favoritesHydrated } = useFavoriteSounds()
  const recent = useRecentPlays()

  // Build candidate pool ONCE per render from all server-rendered sounds. The
  // taste vector resolves favorite IDs through this pool to derive category
  // + vibe signal from saved sounds.
  const candidatePool = useMemo(() => {
    const all: Sound[] = [...trending]
    for (const b of blocks) all.push(...b.picks)
    return all
  }, [trending, blocks])

  const taste: TasteVector = useMemo(
    () =>
      buildTasteVector({
        favoriteSoundIds: favoritesHydrated ? favoriteIds : [],
        recent,
        candidatePool,
      }),
    [favoriteIds, favoritesHydrated, recent, candidatePool],
  )

  // Reorder trending + vibe blocks based on taste. Cold-start (no signal)
  // keeps the original server order via the stable sort.
  const orderedTrending = useMemo(
    () => reorderByTaste(trending, (s) => s, taste),
    [trending, taste],
  )
  const orderedBlocks = useMemo(() => {
    const reorderedBlocks = reorderVibeBlocks(blocks, taste)
    return reorderedBlocks.map((b) => ({
      ...b,
      picks: reorderByTaste(b.picks, (s) => s, taste),
    }))
  }, [blocks, taste])

  // "Because you saved X" rail — uses the favorite sound's vibe overlap with
  // other sounds in the candidate pool. Avoid a server roundtrip; Phase 4
  // adds the collaborative rail with API-fetched also_downloaded.
  const becauseRail = useMemo(() => {
    if (taste.signal === 'cold') return null
    const favoriteInPool = favoriteIds
      .map((id) => candidatePool.find((s) => s.id === id))
      .find((s): s is Sound => Boolean(s))
    if (!favoriteInPool) return null
    const favoriteVibes = new Set(favoriteInPool.vibe_tags ?? [])
    if (favoriteVibes.size === 0) return null
    const peers = candidatePool
      .flatMap((s) =>
        s.id !== favoriteInPool.id && (s.vibe_tags ?? []).some((v) => favoriteVibes.has(v))
          ? [{ s, score: scoreSound(s, taste) }]
          : []
      )
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((x) => x.s)
    if (peers.length === 0) return null
    return { anchor: favoriteInPool, peers }
  }, [taste, favoriteIds, candidatePool])

  const signalCopy = SIGNAL_COPY[taste.signal]

  // §11.1: announce the signal badge's label through the shell's single live
  // region instead of the badge's own aria-live — but only on a real
  // transition (e.g. cold-state → a hydrated taste signal), not on mount,
  // matching the fact that a live region freshly inserted with content isn't
  // reliably announced by AT anyway.
  const prevSignalLabelRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevSignalLabelRef.current !== null && prevSignalLabelRef.current !== signalCopy.label) {
      announce(signalCopy.label, 'polite')
    }
    prevSignalLabelRef.current = signalCopy.label
  }, [signalCopy.label])

  // ── Collaborative rail (Phase 4) ──────────────────────────────────
  // Seeds: the user's favorites + recently completed previews.
  // Excludes: anything the user has already favorited or interacted with.
  const seedIds = useMemo(() => {
    const favs = favoritesHydrated ? favoriteIds : []
    const recentCompleted = recent
      .filter((r) => r.class === 'completed')
      .slice(-5)
      .map((r) => r.id)
    // Dedupe — prefer favorites first (stronger signal).
    const seen = new Set<string>()
    const combined: string[] = []
    for (const id of [...favs, ...recentCompleted]) {
      if (!seen.has(id)) {
        seen.add(id)
        combined.push(id)
      }
      if (combined.length >= 3) break
    }
    return combined
  }, [favoriteIds, favoritesHydrated, recent])

  const excludeIds = useMemo(() => {
    const out = new Set<string>(favoritesHydrated ? favoriteIds : [])
    for (const r of recent) out.add(r.id)
    return [...out]
  }, [favoriteIds, favoritesHydrated, recent])

  const [collabFetch, setCollabFetch] = useState<Sound[] | null>(null)
  const [semanticFetch, setSemanticFetch] = useState<Sound[] | null>(null)

  // Derive what the growing-list pipelines see: when there are no seeds, the
  // last fetched value is stale, so feed `null` instead of resetting state in
  // an effect (which trips react-hooks/set-state-in-effect).
  const activeCollabFetch = seedIds.length === 0 ? null : collabFetch

  // Growing lists — keep prior peers, append new ones with a reveal animation.
  const { items: collabPeers, newIds: collabNewIds } = useGrowingList(activeCollabFetch, 12)

  // Collaborative rail: "Other Tesla owners with similar taste also downloaded"
  // eslint-disable-next-line react-doctor/no-fetch-in-effect -- personalization rails require client-side fetching after hydration; cannot use SWR here without restructuring the entire feed
  // eslint-disable-next-line react-doctor/no-cascading-set-state -- setCollabFetch here feeds useGrowingList; two-stage pipeline is intentional
  useEffect(() => {
    if (seedIds.length === 0) return
    const controller = new AbortController()
    const url = `/api/foryou/also-downloaded?ids=${seedIds.join(',')}&exclude=${excludeIds.join(',')}`
    fetch(url, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : { peers: [] }))
      .then((data: { peers?: Sound[] }) => {
        if (Array.isArray(data.peers) && data.peers.length > 0) {
          setCollabFetch(data.peers)
        } else {
          setCollabFetch(null)
        }
      })
      .catch(() => setCollabFetch(null))
    return () => controller.abort()
  }, [seedIds, excludeIds])

  // Semantic rail: "More like the ones you played" — Harrier OSS v1 1024-dim
  // embedding centroid + HNSW nearest neighbors. Seeds from recent completed
  // previews (better signal than favorites for "what sounds like THIS").
  const semanticSeeds = useMemo(() => {
    const recentCompleted = recent
      .filter((r) => r.class === 'completed')
      .slice(-5)
      .map((r) => r.id)
    if (recentCompleted.length > 0) return recentCompleted
    // Fall back to favorites if no completed plays yet
    return favoritesHydrated ? favoriteIds.slice(0, 3) : []
  }, [recent, favoriteIds, favoritesHydrated])

  const activeSemanticFetch = semanticSeeds.length === 0 ? null : semanticFetch
  const { items: semanticPeers, newIds: semanticNewIds } = useGrowingList(activeSemanticFetch, 12)

  // eslint-disable-next-line react-doctor/no-fetch-in-effect -- semantic rail requires client-side fetching after hydration; cannot use SWR without restructuring
  // eslint-disable-next-line react-doctor/no-cascading-set-state -- setSemanticFetch feeds useGrowingList; two-stage pipeline is intentional
  useEffect(() => {
    if (semanticSeeds.length === 0) return
    const controller = new AbortController()
    const url = `/api/foryou/more-like-this?seeds=${semanticSeeds.join(',')}&exclude=${excludeIds.join(',')}`
    fetch(url, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : { peers: [] }))
      .then((data: { peers?: Sound[] }) => {
        if (Array.isArray(data.peers) && data.peers.length > 0) {
          setSemanticFetch(data.peers)
        } else {
          setSemanticFetch(null)
        }
      })
      .catch(() => setSemanticFetch(null))
    return () => controller.abort()
  }, [semanticSeeds, excludeIds])

  return (
    <>
      {/* Signal indicator — replaces the eyebrow's static "For you — curated
          picks" when hydration finishes. Server-rendered fallback is the
          cold-state label, so first paint stays stable. */}
      <section style={{ padding: '16px 0 0' }}>
        <div className="tls-container">
          {/* eslint-disable-next-line react-doctor/no-inline-exhaustive-style -- dynamic color token (signalCopy.tone) requires inline style; cannot express fully in Tailwind */}
          {/* eslint-disable-next-line react-doctor/no-tiny-text -- 10px label is intentional compact signal indicator; matches TLS design system */}
          {/* §11.1: plain div, not <output> — that element is implicitly
              role="status" (aria-live="polite") per HTML-AAM, which would
              still broadcast on its own even with the aria-live attribute
              removed. The useEffect above routes the real transition through
              the shell's single live region instead. */}
          <div
            className="inline-flex items-center gap-2"
            style={{
              padding: '5px 12px',
              borderRadius: 999,
              border: '1px solid var(--tls-border)',
              background: 'var(--tls-surface)',
              fontFamily: 'var(--tls-font-display)',
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: signalCopy.tone,
            }}
          >
            {signalCopy.label}
          </div>
        </div>
      </section>

      {/* ── Semantic rail (Phase 5) ───────────────────────────────────── */}
      {/* Harrier 1024-dim cosine neighbors of the user's recent completed
          previews. The closest possible content match, finds sounds
          the title-trigram + co-download passes can't see. */}
      {semanticPeers.length > 0 && (
        <section
          style={{
            padding: '48px 0',
            borderBottom: '1px solid var(--tls-border-subtle)',
          }}
        >
          <div className="tls-container">
            <div className="tlsr-section-tick" style={{ marginBottom: 20 }}>
              <div>
                <h2
                  className="tls-h2"
                  style={{ margin: 0, color: 'var(--tls-fg-strong)' }}
                >
                  More like the ones you played
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {semanticPeers.map((sound) => (
                <div
                  key={sound.id}
                >
                  {semanticNewIds.has(sound.id) && <span className="tls-new-label">New</span>}
                  <SoundCard sound={sound} disableEntryAnimation />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Collaborative rail (Phase 4) ──────────────────────────────── */}
      {/* "Other Tesla owners with similar taste also downloaded…" */}
      {collabPeers.length > 0 && (
        <section
          style={{
            padding: '48px 0',
            borderBottom: '1px solid var(--tls-border-subtle)',
          }}
        >
          <div className="tls-container">
            <div className="tlsr-section-tick" style={{ marginBottom: 20 }}>
              <div>
                <h2
                  className="tls-h2"
                  style={{ margin: 0, color: 'var(--tls-fg-strong)' }}
                >
                  Other Tesla owners with similar taste also downloaded
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {collabPeers.map((sound) => (
                <div
                  key={sound.id}
                >
                  {collabNewIds.has(sound.id) && <span className="tls-new-label">New</span>}
                  <SoundCard sound={sound} disableEntryAnimation />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Because you saved X — only when warm/hot ─────────────────── */}
      {becauseRail && (
        <section
          style={{
            padding: '48px 0',
            borderBottom: '1px solid var(--tls-border-subtle)',
          }}
        >
          <div className="tls-container">
            <div className="tlsr-section-tick" style={{ marginBottom: 20 }}>
              <div>
                <h2
                  className="tls-h2"
                  style={{ margin: 0, color: 'var(--tls-fg-strong)' }}
                >
                  More like &ldquo;{becauseRail.anchor.title}&rdquo;
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {becauseRail.peers.map((sound) => (
                <SoundCard
                  key={sound.id}
                  sound={sound}
                 
                  disableEntryAnimation
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Trending ─────────────────────────────────────────────────── */}
      {orderedTrending.length > 0 && (
        <section
          style={{
            padding: '48px 0',
            borderBottom: '1px solid var(--tls-border-subtle)',
          }}
        >
          <div className="tls-container">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 20,
                gap: 16,
              }}
            >
              <div className="tlsr-section-tick" style={{ minWidth: 0 }}>
                <div>
                  <h2
                    className="tls-h2"
                    style={{ margin: 0, color: 'var(--tls-fg-strong)' }}
                  >
                    Most installed this period
                  </h2>
                </div>
              </div>
              <Link
                href={MORE_TRENDING_HREF}
                style={{
                  color: 'var(--tls-fg-muted)',
                  font: 'var(--tls-body-sm)',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  gap: 6,
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                Browse all
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {orderedTrending.map((sound) => (
                <SoundCard
                  key={sound.id}
                  sound={sound}
                 
                  disableEntryAnimation
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Per-vibe sections ────────────────────────────────────────── */}
      {orderedBlocks.map((block, blockIndex) => {
        if (block.picks.length === 0) return null
        const showAd = blockIndex === Math.floor(orderedBlocks.length / 2)
        return (
          <Fragment key={block.vibeSlug}>
            <section
              style={{
                padding: '48px 0',
                borderBottom: '1px solid var(--tls-border-subtle)',
              }}
            >
              <div className="tls-container">
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 20,
                    gap: 16,
                  }}
                >
                  <div className="tlsr-section-tick" style={{ minWidth: 0 }}>
                    <div>
                      <h2
                        className="tls-h2"
                        style={{ margin: 0, color: 'var(--tls-fg-strong)' }}
                      >
                        {block.vibeName}
                      </h2>
                    </div>
                  </div>
                  <Link
                    href={`/sounds/vibe/${block.vibeSlug}`}
                    style={{
                      color: 'var(--tls-fg-muted)',
                      font: 'var(--tls-body-sm)',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      gap: 6,
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    See all {block.vibeName.toLowerCase()}
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {block.picks.map((sound) => (
                    <SoundCard
                      key={sound.id}
                      sound={sound}
                     
                      disableEntryAnimation
                    />
                  ))}
                </div>
              </div>
            </section>
            {showAd && (
              <section
                style={{
                  padding: '24px 0',
                  borderBottom: '1px solid var(--tls-border-subtle)',
                }}
                aria-label="Advertisement"
              >
                <div className="tls-container">
                  <InFeedAd slot="1065739730" />
                </div>
              </section>
            )}
          </Fragment>
        )
      })}
    </>
  )
}
